import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Car, Key, Settings, Shield, CheckCircle, Clock } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const OurWork = ({ currentLang, translations }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [titleRef, titleVisible] = useScrollAnimation();
  const [categoryRef, categoryVisible] = useScrollAnimation();
  const [gridRef, gridVisible] = useScrollAnimation();

  const workCategories = [
    { key: 'all', icon: Car },
    { key: 'keys', icon: Key },
    { key: 'ignition', icon: Settings },
    { key: 'lockout', icon: Shield },
    { key: 'transponder', icon: Settings }
  ];

  const workExamples = [
    {
      id: 1,
      category: 'keys',
      title_en: 'BMW X5 Smart Key Programming',
      title_he: 'תכנות מפתח חכם BMW X5',
      title_ru: 'Программирование смарт-ключа BMW X5',
      description_en: 'Successfully programmed new smart key for 2020 BMW X5. Customer lost original keys during beach visit.',
      description_he: 'תכנות מוצלח של מפתח חכם חדש ל-BMW X5 2020. הלקוח איבד את המפתחות המקוריים במהלך ביקור בחוף.',
      description_ru: 'Успешно запрограммирован новый смарт-ключ для BMW X5 2020 года. Клиент потерял оригинальные ключи во время визита на пляж.',
      duration: '45 min',
      location: 'Tel Aviv - Rothschild Blvd',
      image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 2,
      category: 'lockout',
      title_en: 'Mercedes C-Class Emergency Opening',
      title_he: 'פתיחת חירום מרצדס C-Class',
      title_ru: 'Экстренное вскрытие Mercedes C-Class',
      description_en: 'Emergency lockout service for Mercedes C-Class. Keys locked inside with engine running, opened safely in 18 minutes.',
      description_he: 'שירות חירום לפתיחת מרצדס C-Class. מפתחות ננעלו בפנים עם המנוע פועל, נפתח בבטחה תוך 18 דקות.',
      description_ru: 'Экстренная служба вскрытия Mercedes C-Class. Ключи заперты внутри с работающим двигателем, вскрыт безопасно за 18 минут.',
      duration: '18 min',
      location: 'Ramat Aviv Mall',
      image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 3,
      category: 'keys',
      title_en: 'Tesla Model 3 Key Card Programming',
      title_he: 'תכנות כרטיס מפתח טסלה מודל 3',
      title_ru: 'Программирование ключ-карты Tesla Model 3',
      description_en: 'Programmed new key cards for Tesla Model 3. Original cards malfunctioned after water damage.',
      description_he: 'תכנות כרטיסי מפתח חדשים לטסלה מודל 3. הכרטיסים המקוריים התקלקלו לאחר נזק מים.',
      description_ru: 'Запрограммированы новые ключ-карты для Tesla Model 3. Оригинальные карты вышли из строя после повреждения водой.',
      duration: '35 min',
      location: 'Herzliya Pituach',
      image: 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 4,
      category: 'ignition',
      title_en: 'Volkswagen Golf Ignition Repair',
      title_he: 'תיקון התנעה פולקסווגן גולף',
      title_ru: 'Ремонт зажигания Volkswagen Golf',
      description_en: 'Repaired faulty ignition cylinder in VW Golf 2019. Key was getting stuck and car wouldnt start consistently.',
      description_he: 'תיקון צילינדר התנעה פגום בפולקסווגן גולף 2019. המפתח היה נתקע והמכונית לא התנהלה באופן עקבי.',
      description_ru: 'Отремонтирован неисправный цилиндр зажигания в VW Golf 2019. Ключ заедал и машина не заводилась стабильно.',
      duration: '65 min',
      location: 'Petah Tikva Center',
      image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 5,
      category: 'keys',
      title_en: 'Audi A6 Remote Key Replacement',
      title_he: 'החלפת מפתח רחוק אודי A6',
      title_ru: 'Замена пульта-ключа Audi A6',
      description_en: 'Created and programmed new remote key for Audi A6 2021. Customer dropped original key and internal circuit broke.',
      description_he: 'יצירה ותכנות מפתח רחוק חדש לאודי A6 2021. הלקוח הפיל את המפתח המקורי והמעגל הפנימי נשבר.',
      description_ru: 'Создан и запрограммирован новый пульт-ключ для Audi A6 2021. Клиент уронил оригинальный ключ и внутренняя схема сломалась.',
      duration: '52 min',
      location: 'Dizengoff Square',
      image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 6,
      category: 'lockout',
      title_en: 'Toyota Prius Hybrid Lockout',
      title_he: 'פתיחת טויוטה פריוס היברידית נעולה',
      title_ru: 'Вскрытие заблокированной Toyota Prius Hybrid',
      description_en: 'Emergency opening of Toyota Prius with smart key inside. Special technique used due to hybrid security system.',
      description_he: 'פתיחת חירום של טויוטה פריוס עם מפתח חכם בפנים. שימוש בטכניקה מיוחדת בשל מערכת האבטחה ההיברידית.',
      description_ru: 'Экстренное вскрытие Toyota Prius с умным ключом внутри. Использована специальная техника из-за гибридной системы безопасности.',
      duration: '25 min',
      location: 'Ramat Gan Diamond Exchange',
      image: 'https://images.unsplash.com/photo-1619976215249-83fb7e3e8477?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 7,
      category: 'keys',
      title_en: 'Ford Focus Key Programming',
      title_he: 'תכנות מפתח פורד פוקוס',
      title_ru: 'Программирование ключа Ford Focus',
      description_en: 'Programmed replacement key for Ford Focus 2018. Customer needed second key for family member.',
      description_he: 'תכנות מפתח חלופי לפורד פוקוס 2018. הלקוח נזקק למפתח שני לבן משפחה.',
      description_ru: 'Запрограммирован запасной ключ для Ford Focus 2018. Клиенту нужен был второй ключ для члена семьи.',
      duration: '40 min',
      location: 'Bnei Brak - Jabotinsky',
      image: 'https://images.unsplash.com/photo-1606220838315-056192d5e927?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 8,
      category: 'ignition',
      title_en: 'Mazda CX-5 Ignition Switch Repair',
      title_he: 'תיקון מתג התנעה מאזדה CX-5',
      title_ru: 'Ремонт замка зажигания Mazda CX-5',
      description_en: 'Replaced worn ignition switch in Mazda CX-5. Key wouldnt turn due to internal mechanism failure.',
      description_he: 'החלפת מתג התנעה בלוי במאזדה CX-5. המפתח לא הסתובב בגלל כשל במנגנון הפנימי.',
      description_ru: 'Заменен изношенный замок зажигания в Mazda CX-5. Ключ не поворачивался из-за отказа внутреннего механизма.',
      duration: '75 min',
      location: 'Netanya Industrial Area',
      image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 9,
      category: 'lockout',
      title_en: 'Nissan Qashqai Emergency Access',
      title_he: 'גישה חירום ניסאן קשקאי',
      title_ru: 'Экстренный доступ к Nissan Qashqai',
      description_en: 'Opened locked Nissan Qashqai with baby inside during summer heat. Quick 12-minute response with no damage.',
      description_he: 'פתיחת ניסאן קשקאי נעולה עם תינוק בפנים במהלך חום הקיץ. תגובה מהירה של 12 דקות ללא נזק.',
      description_ru: 'Вскрыт заблокированный Nissan Qashqai с ребенком внутри в летнюю жару. Быстрый 12-минутный ответ без повреждений.',
      duration: '12 min',
      location: 'Ashdod Shopping Center',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 10,
      category: 'keys',
      title_en: 'Kia Sportage Smart Key Creation',
      title_he: 'יצירת מפתח חכם קיה ספורטאז',
      title_ru: 'Создание умного ключа Kia Sportage',
      description_en: 'Created new smart key for Kia Sportage 2020. Customer lost key during hiking trip in Jerusalem hills.',
      description_he: 'יצירת מפתח חכם חדש לקיה ספורטאז 2020. הלקוח איבד את המפתח במהלך טיול הליכה בהרי ירושלים.',
      description_ru: 'Создан новый умный ключ для Kia Sportage 2020. Клиент потерял ключ во время пешего похода в Иерусалимских холмах.',
      duration: '48 min',
      location: 'Jerusalem - Malha Mall',
      image: 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 11,
      category: 'transponder',
      title_en: 'Chevrolet Cruze Transponder Programming',
      title_he: 'תכנות טרנספונדר שברולט קרוז',
      title_ru: 'Программирование транспондера Chevrolet Cruze',
      description_en: 'Programmed new transponder chip for Chevrolet Cruze. Original chip damaged in washing machine accident.',
      description_he: 'תכנות שבב טרנספונדר חדש לשברולט קרוז. השבב המקורי ניזוק בתאונת מכונת כביסה.',
      description_ru: 'Запрограммирован новый транспондерный чип для Chevrolet Cruze. Оригинальный чип поврежден в стиральной машине.',
      duration: '55 min',
      location: 'Holon - Azrieli Towers Area',
      image: 'https://images.unsplash.com/photo-1580414052520-025ef74db2ef?w=400&h=250&fit=crop&q=80'
    },
    {
      id: 12,
      category: 'lockout',
      title_en: 'Subaru Outback Lockout Service',
      title_he: 'שירות פתיחת סובארו אאוטבק נעולה',
      title_ru: 'Служба вскрытия Subaru Outback',
      description_en: 'Professional lockout service for Subaru Outback at Tel Aviv Port. Keys left inside during business meeting.',
      description_he: 'שירות פתיחה מקצועי לסובארו אאוטבק בנמל תל אביב. מפתחות נשארו בפנים במהלך פגישת עסקים.',
      description_ru: 'Профессиональная служба вскрытия Subaru Outback в порту Тель-Авива. Ключи оставлены внутри во время деловой встречи.',
      duration: '22 min',
      location: 'Tel Aviv Port',
      image: 'https://images.unsplash.com/photo-1606220838315-056192d5e927?w=400&h=250&fit=crop&q=80'
    }
  ];

  const filteredWorks = activeCategory === 'all' 
    ? workExamples 
    : workExamples.filter(work => work.category === activeCategory);

  const getTitle = (work) => work[`title_${currentLang}`];
  const getDescription = (work) => work[`description_${currentLang}`];

  return (
    <section id="ourwork" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div 
          ref={titleRef}
          className={`max-w-4xl mx-auto text-center mb-16 scroll-animate ${titleVisible ? 'visible' : ''}`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {translations[currentLang].ourWork.title}
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            {translations[currentLang].ourWork.subtitle}
          </p>
        </div>

        {/* Category Filter */}
        <div 
          ref={categoryRef}
          className={`flex flex-wrap justify-center gap-4 mb-12 scroll-animate-scale ${categoryVisible ? 'visible' : ''}`}
        >
          {workCategories.map((category) => {
            const IconComponent = category.icon;
            return (
              <Button
                key={category.key}
                variant={activeCategory === category.key ? "default" : "outline"}
                onClick={() => setActiveCategory(category.key)}
                className={`px-6 py-3 flex items-center space-x-2 rtl:space-x-reverse ${
                  activeCategory === category.key 
                    ? 'bg-blue-600 text-white' 
                    : 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
                }`}
              >
                <IconComponent className="w-4 h-4" />
                <span>{translations[currentLang].ourWork.categories[category.key]}</span>
              </Button>
            );
          })}
        </div>

        {/* Work Examples Grid */}
        <div 
          ref={gridRef}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-8 ${gridVisible ? 'visible' : ''}`}
        >
          {filteredWorks.map((work, index) => (
            <Card 
              key={work.id} 
              className={`group hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:scale-105 scroll-animate stagger-${(index % 6) + 1} ${gridVisible ? 'visible' : ''}`}
            >              
              <CardContent className="p-8 space-y-4">
                <h3 className="text-xl font-bold text-gray-900 line-clamp-2">
                  {getTitle(work)}
                </h3>
                
                <p className="text-gray-600 leading-relaxed line-clamp-3">
                  {getDescription(work)}
                </p>
                
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{work.duration}</span>
                  </div>
                  
                  <div className="flex items-center space-x-1 rtl:space-x-reverse">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-600">
                      {translations[currentLang].ourWork.completed}
                    </span>
                  </div>
                </div>
                
                <div className="text-sm text-blue-600 font-medium">
                  📍 {work.location}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {translations[currentLang].ourWork.cta.title}
            </h3>
            <p className="text-gray-600 mb-6">
              {translations[currentLang].ourWork.cta.description}
            </p>
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg flex items-center space-x-3 rtl:space-x-reverse mx-auto"
              onClick={() => window.open('tel:+972553002025')}
            >
              <Key className="w-5 h-5" />
              <span>{translations[currentLang].ourWork.cta.button}</span>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurWork;